import { useSignOut } from "../../hooks/use-sign-out";
import { childActivityUrl } from "../../lib/navigation";
import fox from "../../assets/reading-fox.png";
import { ArrowRight, LogOut } from "lucide-react";
import { useEffect, useState } from "react";
import { getPalette } from "../../lib/child-presets";
import type { ChildProfile } from "../../types";
import "./KidsHome.scss";

type ChildStat = {
  totalStars: number;
  totalResponses: number;
};

type Props = {
  children: ChildProfile[];
  onBack?: () => void;
};

function statLine(child: ChildProfile, stat: ChildStat | null | undefined): string {
  const base = `אוצר מילים · שלב ${child.currentStep}`;
  if (!stat || stat.totalResponses === 0) return base;
  return `${base} · ⭐ ${stat.totalStars}`;
}

export default function KidsHome({ children, onBack }: Props) {
  const [statsMap, setStatsMap] = useState<Record<string, ChildStat>>({});

  const { logout: handleSignOut, error: logoutError, pending: loggingOut } = useSignOut();

  useEffect(() => {
    children.forEach((child) => {
      fetch(`/api/children/${child.id}/stats`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data) {
            setStatsMap((prev) => ({
              ...prev,
              [child.id]: { totalStars: data.totalStars, totalResponses: data.totalResponses },
            }));
          }
        })
        .catch(() => {});
    });
  }, [children]);

  return (
    <main id="main-content" className="kids-home" dir="rtl">
      {logoutError && <p role="alert">{logoutError}</p>}
      <div className="kids-home-header">
        {onBack && (
          <button className="kids-home-back" onClick={onBack} aria-label="חזרה לניהול">
            <ArrowRight className="brand-icon"  aria-hidden="true" strokeWidth={2.4} />
          </button>
        )}
        <img className="brand-fox" src={fox} alt="" /><h1 className="kids-home-title">מי לומד היום?</h1>
        <button
          className="kids-home-signout"
          onClick={handleSignOut} disabled={loggingOut}
          aria-label="התנתקות"
          title="התנתקות"
        >
          <LogOut className="brand-icon" aria-hidden="true" strokeWidth={2.4} />
        </button>
      </div>

      {children.length === 0 ? (
        <p className="kids-home-empty">אין ילדים עדיין. <a href="/">הוספת ילד/ה בניהול</a></p>
      ) : (
        <div className={`kids-home-grid ${children.length === 1 ? "single" : ""}`}>
          {children.map((child) => {
            const palette = getPalette(child.colorPalette);
            const stat = statsMap[child.id] ?? null;
            return (
              <a
                key={child.id}
                href={childActivityUrl(child.magicToken)}
                className="kids-home-card"
                style={{ "--card-color": palette.primary, "--card-bg": palette.primaryBg } as React.CSSProperties}
              >
                {child.emoji ? (
                  <span className="kids-home-card-emoji">{child.emoji}</span>
                ) : (
                  <span
                    className="kids-home-card-avatar"
                    style={{ background: palette.primary }}
                  >
                    {child.name.charAt(0)}
                  </span>
                )}
                <span className="kids-home-card-name">{child.name}</span>
                <span className="kids-home-card-stat">{statLine(child, stat)}</span>
              </a>
            );
          })}
        </div>
      )}
    </main>
  );
}
